/**
 * Geht das Mikro nach Vesperas Antwort von selbst auf? — EINE Entscheidung für
 * das Gerät, ihre Stufen und Jevs Meldung vom Server. (07.10.2026, Ticket 3048)
 *
 * Martin, 07.10. 07:52: *„Die Stufe ‚immer‘ war nie so gemeint, dass das Mikro
 * wirklich immer aufgeht. Gemeint war der Jev-Modus: Jev entscheidet, wann
 * Schluss ist, auch wenn Vesperas Antwort keine Frage ist.“* „Immer“ wird darum
 * durch „Jev entscheidet“ ERSETZT; „Nach Frage“ bleibt, wie es war.
 *
 * Befund (Journal 72 h, Konto 1): Das Gerät stand auf „immer“ — das Mikro ging
 * nach JEDER Antwort auf (128-mal). Jev konnte es nur über das Ende-Signal
 * schließen (gespraech_beenden), und das nur, wenn er sich sehr sicher war.
 *
 * DIE STUFEN (gespeichert unter `autoMikroNachFrage`, am Gerät und am Server):
 *
 *   false   aus (die Vorgabe)
 *   true    nur nach einer Frage — „Nach Frage“
 *   "jev"   Jev entscheidet — ersetzt „immer“; ein gespeichertes "immer" wird
 *           beim Lesen still zu "jev" (`autoMikroStufe`)
 *
 * WAS „JEV ENTSCHEIDET“ NACH EINER ANTWORT TUT (`mikroNachAntwort`):
 *
 *   1. Er hat sich verabschiedet (Ende-Signal im Zug) → zu, in jeder Stufe. Welle 1: gilt für sein AUSDRÜCKLICHES Ende;
 *      ein nur von Jev abgeleitetes wirkt allein in „jev“ und nie bei einer Frage am Ende (`abschiedAbgeleitet`).
 *   2. Wartet etwas auf ihn (Frage am Ende, wartende Fragen-Runde) → auf wie
 *      bei „Nach Frage“. Die Frage-Regel ist die feste Basis, Jev nur der Rand.
 *   3. Sonst die Meldung des Servers (`JevMikro`, Ereignis `mikro`):
 *        zu     Jev ist sicher, dass das Gespräch zu Ende ist → zu
 *        kurz   vermutlich Schluss (Jev 0,50 bis unter 0,90) → auf, aber nur
 *               JEV_KURZ_MS lang ohne Sprache
 *        offen  Jev sagt „geht weiter“ (unter 0,50), Fehler, zu spät, Ausfall
 *               → auf wie bisher bei „immer“
 *      Keine Meldung (älterer Server, anderes Konto, Chat-Weg) → offen wie
 *      bisher. Im Zweifel offen: ein abgeschnittener Satz ist schlimmer als
 *      ein Mikro, das ein paar Sekunden zuhört.
 *
 * ⚠️ Die Meldung des Servers wirkt NUR in der Stufe „jev“. „Nach Frage“ und
 *    „aus“ verhalten sich genau wie vorher.
 * ⚠️ „zu“ kommt in der Praxis schon über das Ende-Signal (Punkt 1) — die
 *    Meldung sagt es zusätzlich, damit die Protokollzeile den Grund kennt.
 *
 * ARBEITSZUG (15.10.2026, Plan „Verlässliche Abarbeitung“, Welle A): Martin, nach einem Blender-Nachmittag — das Mikro ging
 * mitten im Arbeitszug auf und schickte Raumton und Flüche als neuen Auftrag ab. Der Zugvertrag (server/zug-vertrag.ts)
 * meldet am Zugende `zug` ({art, rueckfrage}). Nach einem Arbeitszug (`arbeitszug`) zählt eine Frage am Ende NICHT mehr
 * als „wartet auf Martin“ — das war meist eine Floskel („Soll ich noch …?“). Auf geht es nur, wenn wirklich eine
 * Entscheidung wartet (Rückfrage-Karte, Ja-Tor, Fragen-Runde); sonst zu, in jeder Stufe. Plauder-Züge wie bisher.
 * Dass das Mikro überhaupt erst nach `done` entscheidet, sorgt frontend/lib/zug-ende.ts.
 * 16.10.2026 (Martin: „Wenn du eine Frage stellst, soll das Mikrofon immer aufgehen“): ÜBERHOLT den Satz „zählt NICHT
 * mehr“ oben — endet ein Arbeitszug mit einer echten Frage (`wartetAufMartin`, wie im Plauderzug), geht das Mikro auf;
 * ohne Frage bleibt er zu.
 * 17.10.2026 (Martin 05:21, „… nicht fertig geworden. Frag mich bitte noch einmal.“ — Mikro blieb zu, grund=arbeit):
 * endet der Zug mit dem Notsatz der Schlussrunde, meldet der Server `zug.erwartetAntwort` (server/werkzeug-schlussrunde.ts,
 * fester Satz, kein Raten am Text). Das ist eine Bitte an Martin — sie öffnet wie eine Frage, auch nach einem Arbeitszug
 * (`grund=notsatz-bitte`). Eine Fertigmeldung oder Floskel des Modells trägt das Feld nie.
 * 28.10.2026 (Autonomie-Plan B5a): wartet der Zug NUR auf eine Knopf-Karte (Mandat, Prozess-Freigabe, Zugangswerte — Meldung
 * `zug.nurKnopf`), ist der Knopf die einzige Antwort: das Mikro geht nicht von selbst auf, auch nicht mit einer Frage am Ende
 * (`grund=knopf-karte`). Ein gesprochenes Ja gälte dort ohnehin nie. Entwickler-Karten (Schmied/Zimmermann) regelt weiter
 * frontend/lib/ja-karte-offen.ts. Das Ja-Tor bleibt unberührt — hier wird nur NICHT geöffnet.
 */

import type { Wiedergabe } from "./wiedergabe.js";
import { nachlauschNachFrage } from "./nachlausch.js";

/** Die Stufen des Schalters „Mikro von selbst öffnen“. */
export type AutoMikroStufe = boolean | "jev";

/** In dieser Reihenfolge schaltet der Knopf im Mikrofon-Fenster weiter. */
export const AUTO_MIKRO_STUFEN: readonly AutoMikroStufe[] = [false, true, "jev"];

/**
 * Der gespeicherte Wert als Stufe — `undefined`, wenn er keine ist.
 * ⚠️ "immer" (bis 07.10.2026) wird still zu "jev": Martins Entscheidung, keine Rückfrage.
 */
export function autoMikroStufe(roh: unknown): AutoMikroStufe | undefined {
  if (typeof roh === "boolean") return roh;
  if (roh === "jev" || roh === "immer") return "jev";
  return undefined;
}

/**
 * Die Stufen, die ein Konto zur Wahl hat — „Jev entscheidet“ nur mit Jev (Konto 1, frontend/lib/jev-konto.ts).
 * (20.10.2026, Martin: andere Konten sehen die Stufe nicht, der Server fragt Jev dort nie.)
 */
export function autoMikroStufenFuer(mitJev: boolean): readonly AutoMikroStufe[] {
  return mitJev ? AUTO_MIKRO_STUFEN : AUTO_MIKRO_STUFEN.filter((s) => s !== "jev");
}

/** Die Stufe, die für dieses Konto gilt: ein gespeichertes „jev“ ohne Jev wird als „Nach Frage“ gelesen. */
export function autoMikroStufeFuerKonto(stufe: AutoMikroStufe | undefined, mitJev: boolean): AutoMikroStufe | undefined {
  return stufe === "jev" && !mitJev ? true : stufe;
}

/** Die nächste Stufe; ein unbekannter Wert führt auf „aus". Ohne Jev (`mitJev` false) fehlt „jev“ in der Reihe. */
export function naechsteAutoMikro(stufe: AutoMikroStufe | undefined, mitJev = true): AutoMikroStufe {
  const reihe = autoMikroStufenFuer(mitJev);
  const i = reihe.indexOf(autoMikroStufeFuerKonto(stufe, mitJev) ?? false);
  return i < 0 ? false : reihe[(i + 1) % reihe.length] ?? false;
}

/** Was Jev am Ende des Zugs über das Mikro meldet. */
export type JevMikro = "zu" | "kurz" | "offen";

/**
 * Das kurze Fenster bei „vermutlich Schluss“: so lange hört das Mikro ab dem
 * wirklich offenen Mikro zu; kommt keine Sprache, geht es ohne Senden zu.
 * Sobald er spricht, gilt das normale Verhalten (Nachlausch-Fenster,
 * frontend/lib/ptt-modal.ts `nachlauschenZu`).
 *
 * Warum 3 s: die untere Kante des Vorschlags vom 07.10. (drei bis vier
 * Sekunden) und Martins eigenes Fenster „Kurz zuhören“ (3 s bei 158 von 158
 * Öffnungen im Journal 04.–07.10.). Länger als sein eigenes Fenster darf
 * „kurz“ nie sein (`kurzFenster`) — sonst hörte „vermutlich Schluss“ länger zu
 * als „geht weiter“.
 */
export const JEV_KURZ_MS = 3000;

/** Die Länge des kurzen Fensters zu einem eingestellten Fenster (0 = aus = offen ohne Frist). */
export function kurzFenster(eingestelltMs: number): number {
  return eingestelltMs > 0 ? Math.min(eingestelltMs, JEV_KURZ_MS) : JEV_KURZ_MS;
}

/**
 * Die Meldung des Servers (Ereignis `mikro`, vor `done`): die Stufe, sein Grund
 * (ein Wort aus server/jev-abschluss.ts) und Jevs „abgeschlossen“ (0–1).
 */
export interface JevMikroMeldung {
  stufe: JevMikro;
  grund: string | null;
  jev: number | null;
}

/** Was der Zugvertrag am Zugende meldet (Ereignis `zug`, server/zug-vertrag.ts). */
export interface ZugMeldung {
  art: "arbeit" | "plauder";
  /** Wartet eine echte Entscheidung (ask_confirm, Ja-Tor)? */
  rueckfrage: boolean;
  /** 16.10.2026: Vespera verabschiedet am Ende den Schmied — das Mikro wartet nicht auf ihn (server/dreier-modus.ts). Nur `true`. */
  schmiedAbschied?: true;
  /** 17.10.2026: der Zug endet mit dem Notsatz („Frag mich bitte noch einmal“) — eine Bitte an Martin. Nur `true`. */
  erwartetAntwort?: true;
  /** 20.10.2026: der Zug hat einen Termin/eine Erinnerung/einen Anruf erfolgreich angelegt, ohne Rückfrage und ohne Fehler. Nur `true`. */
  aktionErledigt?: true;
  /** 28.10.2026 (B5a): die offene Entscheidung ist NUR eine Knopf-Karte (Mandat, Prozess, Zugangswerte) — Antwort nur per Knopf. Nur `true`. */
  nurKnopf?: true;
}

/** Die Meldung lesen — ohne bekannte Art ist es „keine Meldung“ (`null`, dann wie bisher). */
export function zugMeldungAus(roh: unknown): ZugMeldung | null {
  const r = roh as { art?: unknown; rueckfrage?: unknown; schmiedAbschied?: unknown; erwartetAntwort?: unknown; aktionErledigt?: unknown; nurKnopf?: unknown } | null;
  if (r?.art !== "arbeit" && r?.art !== "plauder") return null;
  return {
    art: r.art,
    rueckfrage: r.rueckfrage === true,
    ...(r.schmiedAbschied === true ? { schmiedAbschied: true as const } : {}),
    ...(r.erwartetAntwort === true ? { erwartetAntwort: true as const } : {}),
    ...(r.aktionErledigt === true ? { aktionErledigt: true as const } : {}),
    // B5a: nur zusammen mit einer Rückfrage sinnvoll — ohne sie wartet keine Karte.
    ...(r.nurKnopf === true && r.rueckfrage === true ? { nurKnopf: true as const } : {}),
  };
}

/** Die Meldung lesen — ohne eine der drei Stufen ist es „keine Meldung“ (`null`). */
export function jevMikroAus(roh: unknown): JevMikroMeldung | null {
  const r = roh as { stufe?: unknown; grund?: unknown; jev?: unknown } | null;
  const s = r?.stufe;
  if (s !== "zu" && s !== "kurz" && s !== "offen") return null;
  const grund = typeof r?.grund === "string" && /^[a-z-]{1,24}$/.test(r.grund) ? r.grund : null;
  const jev = typeof r?.jev === "number" && Number.isFinite(r.jev) && r.jev >= 0 && r.jev <= 1 ? r.jev : null;
  return { stufe: s, grund, jev };
}

type MikroGrund =
  | "aus"
  | "abschied"
  | "frage"
  | "keine-frage"
  | "jev-zu"
  | "jev-kurz"
  | "jev-offen"
  | "ohne-jev"
  | "musik"
  | "musik-ende"
  | "lange-spur"
  | "arbeit"
  | "notsatz-bitte"
  | "aktion-erledigt"
  | "fertig-gedrueckt"
  | "knopf-karte";

export interface MikroNachAntwort {
  /** Mit welchem Anlass das Mikro aufgeht; `null` = es bleibt zu. */
  via: "nach-frage" | "nach-antwort" | null;
  /** Das Nachlausch-Fenster für dieses Öffnen (ms); 0 = ohne Frist. Bei `via: null` immer 0. */
  fensterMs: number;
  /** Für die Protokollzeile: zu, kurz oder offen. */
  entscheid: JevMikro;
  grund: MikroGrund;
}

const zu = (grund: MikroGrund): MikroNachAntwort => ({ via: null, fensterMs: 0, entscheid: "zu", grund });

/**
 * DIE Entscheidung nach dem Vorlese-Ende — rein, ohne Gerät und Netz.
 *
 * `fensterMs` ist das eingestellte Nachlausch-Fenster (shared/nachlausch.ts,
 * schon geprüft); „offen“ übernimmt es, „kurz“ kürzt es auf `kurzFenster`.
 */
export function mikroNachAntwort(a: {
  stufe: AutoMikroStufe | undefined;
  /** Endet die Antwort mit einer Frage, oder wartet eine Fragen-Runde auf ihn? */
  wartetAufMartin: boolean;
  /** Ging in diesem Zug das Ende-Signal ein (Abschied, Jevs Abschluss, Modell)? */
  verabschiedet: boolean;
  /**
   * Welle 1: das Ende-Signal war von Jev nur abgeleitet (Abschied, Aussage, Abschluss) — kein ausdrückliches Ende von Martin
   * (Modell-Werkzeug, Taste „Fertig“). Fehlt: ausdrücklich (wie bisher).
   */
  abschiedAbgeleitet?: boolean;
  /** Jevs Meldung für diesen Zug; `null` = keine. */
  jev: JevMikro | null;
  fensterMs: number;
  /** Läuft Musik oder ein Video? Dann bleibt es zu — es würde die Wiedergabe aufnehmen (Tickets 3232/3230). */
  wiedergabe?: Wiedergabe;
  /**
   * 17.10.2026 (Martin: „Meditation läuft … kein Mikrofon, sonst nimmt er sich selbst auf“): läuft eine LANGE SPUR
   * (Meditation, Spiegel, Biografie, Musik; frontend/lib/lange-spur.ts)? Dann bleibt das Mikro zu, in jeder Stufe.
   */
  langeSpur?: boolean;
  /** Die Musik ist gerade zu Ende (kein Vorlese-Ende): nur „Jev entscheidet“ öffnet, kurz - nie stärker als nach einer Antwort. */
  musikEnde?: boolean;
  /**
   * Der Zug war ein Arbeitszug (Meldung `zug` art=arbeit). `entscheidungOffen`: Rückfrage-Karte, Ja-Tor oder wartende
   * Fragen-Runde. Fehlt die Angabe (Plauderzug, älterer Server), gilt die Regel wie bisher.
   */
  arbeitszug?: { entscheidungOffen: boolean };
  /**
   * 17.10.2026: der Zug endet mit dem Notsatz, der Martin bittet, noch einmal zu fragen (Meldung `zug.erwartetAntwort`).
   * Öffnet wie eine Frage — in „Nach Frage“ und „Jev entscheidet“, auch nach einem Arbeitszug. Fehlt es: wie bisher.
   */
  notsatzBitte?: boolean;
  /**
   * 17.10.2026 (Martin, 09:45: „Fertig“): er hat in diesem Zug „Fertig“ gedrückt (frontend/lib/fertig.ts) — die Antwort
   * gilt als abgeschlossen, das Mikro bleibt zu. In JEDER Stufe, vor jeder anderen Regel (auch vor einer Frage am Ende).
   */
  fertig?: boolean;
  /**
   * 20.10.2026 (Martin 08:35, Termin „ruf mich um 9:55 an“ angelegt, „Erledigt …“ — das Mikro ging trotzdem auf): der Zug
   * endete mit einer erfolgreich angelegten Aktion (Meldung `zug.aktionErledigt`). Der Vorgang ist abgeschlossen: zu — aber
   * erst NACH den Frage-Regeln (Frage am Ende, offene Fragen-Runde, Notsatz-Bitte öffnen weiter). Notschalter ZUG_ERLEDIGT_ZU=0.
   */
  aktionErledigt?: boolean;
  /**
   * 28.10.2026 (B5a): die offene Entscheidung des Zugs ist NUR eine Knopf-Karte (Meldung `zug.nurKnopf`: Mandat, Prozess-Freigabe,
   * Zugangswerte). Ihre Antwort ist allein der Knopf — das Mikro bleibt zu, in jeder Stufe, auch mit einer Frage am Ende.
   */
  nurKnopf?: boolean;
}): MikroNachAntwort {
  if (a.fertig) return zu("fertig-gedrueckt");
  // Welle 1: Martins ausdrückliches Ende gilt in jeder Stufe. Ein nur von Jev abgeleitetes wirkt allein in der Stufe „jev“
  // und nie bei einer Frage am Ende — sonst fällt es durch auf die Regeln unten (im Zweifel bleibt das Mikro offen).
  if (a.verabschiedet && (!a.abschiedAbgeleitet || (a.stufe === "jev" && !a.wartetAufMartin))) return zu("abschied");
  if (a.stufe !== true && a.stufe !== "jev") return zu("aus");
  if (a.nurKnopf) return zu("knopf-karte");
  if (a.langeSpur) return zu("lange-spur");
  if (a.wiedergabe && a.wiedergabe !== "stille") return zu("musik");
  if (a.musikEnde) {
    return a.stufe === "jev"
      ? { via: "nach-antwort", fensterMs: kurzFenster(a.fensterMs), entscheid: "kurz", grund: "musik-ende" }
      : zu("keine-frage");
  }
  // Arbeitszug: öffnet nur mit einer Frage am Ende oder offener Entscheidung — Jevs Meldung allein nicht.
  const frage: MikroNachAntwort = { via: "nach-frage", fensterMs: nachlauschNachFrage(a.fensterMs), entscheid: "offen", grund: "frage" };
  // Der Notsatz bittet um eine neue Frage — er wartet auf Martin wie eine Frage, nur mit eigenem Grund im Protokoll.
  const bitte: MikroNachAntwort = { ...frage, grund: "notsatz-bitte" };
  if (a.arbeitszug) {
    if (a.arbeitszug.entscheidungOffen || a.wartetAufMartin) return frage;
    return a.notsatzBitte ? bitte : zu("arbeit");
  }
  if (a.wartetAufMartin) return frage;
  if (a.notsatzBitte) return bitte;
  if (a.aktionErledigt) return zu("aktion-erledigt");
  if (a.stufe === true) return zu("keine-frage");
  if (a.jev === "zu") return zu("jev-zu");
  if (a.jev === "kurz") {
    return { via: "nach-antwort", fensterMs: kurzFenster(a.fensterMs), entscheid: "kurz", grund: "jev-kurz" };
  }
  return { via: "nach-antwort", fensterMs: a.fensterMs, entscheid: "offen", grund: a.jev === "offen" ? "jev-offen" : "ohne-jev" };
}

/**
 * Die Protokollzeile des Geräts — nur Wörter und Zahlen, kein Text.
 *
 *   [mikro-nach-antwort] stufe=jev entscheid=kurz grund=jev-kurz jev=0.62 server=vermutlich fenster=3000
 *
 * `jev=` Jevs „abgeschlossen“, `server=` der Grund seiner Meldung; `-` = keine Meldung.
 */
export function mikroNachAntwortZeile(
  e: MikroNachAntwort,
  stufe: AutoMikroStufe | undefined,
  meldung: JevMikroMeldung | null,
  /** Welle 0: endete Vesperas Antwort mit einer Frage (`endetMitFrage` der Vorlese-Schlange)? Fehlt: kein Feld (alte Zeilenform). */
  frageAmEnde?: boolean,
  /** Welle 1: das Ende-Signal dieses Zugs war von Jev nur abgeleitet (`abl=1`; kein Feld sonst). Mit `grund=abschied` gewirkt, sonst übergangen. */
  abgeleitet?: boolean,
): string {
  const s = stufe === "jev" ? "jev" : stufe === true ? "frage" : "aus";
  const jev = meldung?.jev == null ? "-" : meldung.jev.toFixed(2);
  return (
    `[mikro-nach-antwort] stufe=${s} entscheid=${e.entscheid} grund=${e.grund}` +
    ` jev=${jev} server=${meldung?.grund ?? "-"} fenster=${e.fensterMs}` +
    (frageAmEnde === undefined ? "" : ` frageAmEnde=${frageAmEnde ? 1 : 0}`) +
    (abgeleitet ? " abl=1" : "")
  );
}
